import BenefitGuide from 'model/BenefitGuide';
import PlanReference from 'model/PlanReference';
import PlansForDate from 'modules/effectiveDate/types';
import { BenefitSection, BENEFIT_TYPES } from 'modules/home/constants';

export type RemovedPlans = {
  section: string;
  label: string;
  plans: string[];
  // At least one line known to this client is left with no plan.
  emptied: boolean;
  // At least one known emptied line has defaults on the new date.
  refilled: boolean;
  // Another known line stays populated, or a removed line cannot be resolved.
  partiallyEmptied?: boolean;
  // Some emptied lines get defaults while others do not.
  partiallyRefilled?: boolean;
};

const SINGLE_LINE_DEFAULT_KIND: Partial<Record<BenefitSection, string>> = {
  [BenefitSection.MEDICAL]: 'MEDICAL',
  [BenefitSection.DENTAL]: 'DENTAL',
  [BenefitSection.VISION]: 'VISION',
  [BenefitSection.HRA]: 'HRA',
  [BenefitSection.HSA]: 'HSA',
  [BenefitSection.FOUR_O_ONE_K]: 'RETIREMENT_401K',
};

// Hidden Spanish copies are never listed for a date, so a copy is judged by the
// English plan it came from. An English guide's plan can name a source too (a
// plan carried to another year), and is still judged by itself.
const judgedId = (reference: PlanReference, isSpanish: boolean) =>
  (isSpanish && reference.sourcePlanId) || reference.planId;

// Approximates the server preview from the smaller edit response. Absence from
// plans-for-date is the signal for an attached plan that may be removed, unless
// the response names the plan's own plan year.
export const findRemovedPlans = (
  guide: BenefitGuide,
  forDate: PlansForDate,
  enabledSections: string[] = Object.values(BenefitSection)
): RemovedPlans[] => {
  const enabled = new Set(enabledSections);
  const isSpanish = guide.language === 'SPANISH';
  const activeIds = new Set(
    forDate.lines.flatMap((line) => line.plans.map((plan) => plan.masterId))
  );
  const defaultKinds = new Set(
    forDate.lines
      .filter((line) =>
        line.plans.some((plan) => plan.default && plan.masterId !== null)
      )
      .map((line) => line.benefitKind)
  );

  // Plans-for-date never lists a hidden plan, so a plan missing from it is
  // still kept when its own plan year covers the date on its line (the
  // server's rule). An older server sends no plan year; then absence decides.
  const ownYearCovers = (reference: PlanReference) => {
    if (!reference.planYearId) {
      return false;
    }
    return forDate.lines
      .filter(
        (line) =>
          !reference.benefitKind || line.benefitKind === reference.benefitKind
      )
      .some((line) =>
        line.planYears.some((year) => year.id === reference.planYearId)
      );
  };

  // The server keeps a Spanish copy that has no link to an English plan.
  const isKept = (reference: PlanReference) =>
    (isSpanish && !reference.sourcePlanId) ||
    activeIds.has(judgedId(reference, isSpanish)) ||
    (!(isSpanish && reference.sourcePlanId) && ownYearCovers(reference));

  return Object.values(BenefitSection).flatMap((section) => {
    if (!enabled.has(section)) {
      return [];
    }
    const attached: PlanReference[] = (guide as any)[section]?.plans ?? [];
    const removed = attached.filter((reference) => !isKept(reference));
    if (removed.length === 0) {
      return [];
    }

    const candidateKinds = (reference: PlanReference) => {
      const listedKinds = forDate.lines
        .filter(
          (line) =>
            Boolean(line.benefitKind) &&
            line.plans.some(
              (plan) => plan.masterId === judgedId(reference, isSpanish)
            )
        )
        .map((line) => line.benefitKind);
      if (listedKinds.length > 0) {
        return new Set([
          ...listedKinds,
          ...(reference.benefitKind ? [reference.benefitKind] : []),
        ]);
      }
      if (reference.benefitKind) {
        return new Set([reference.benefitKind]);
      }
      const defaultKind = SINGLE_LINE_DEFAULT_KIND[section];
      return defaultKind ? new Set([defaultKind]) : new Set<string>();
    };
    const kept = attached.filter(isKept);
    const removedCandidates = removed.map(candidateKinds);
    const keptCandidates = kept.map(candidateKinds);
    const hasUnknownRemoved = removedCandidates.some(
      (candidates) => candidates.size === 0
    );
    const removedKinds = new Set(
      removedCandidates.flatMap((candidates) => Array.from(candidates))
    );
    const keptKinds = new Set(
      keptCandidates.flatMap((candidates) => Array.from(candidates))
    );
    const hasUnknownKept = keptCandidates.some(
      (candidates) => candidates.size === 0
    );
    const emptiedKinds = hasUnknownKept
      ? []
      : Array.from(removedKinds).filter((kind) => !keptKinds.has(kind));
    const emptied = emptiedKinds.length > 0;
    // The defaults are English plans, which a Spanish guide never takes.
    const refilledKinds = isSpanish
      ? []
      : emptiedKinds.filter((kind) => defaultKinds.has(kind));
    const refilled = refilledKinds.length > 0;
    const partiallyEmptied =
      emptied && (keptKinds.size > 0 || hasUnknownRemoved);
    const partiallyRefilled =
      refilled && refilledKinds.length < emptiedKinds.length;
    return [
      {
        section,
        label: BENEFIT_TYPES[section].label,
        plans: removed
          .map((reference) => reference.planName ?? reference.name)
          .filter((name): name is string => Boolean(name)),
        emptied,
        refilled,
        ...(partiallyEmptied ? { partiallyEmptied: true } : {}),
        ...(partiallyRefilled ? { partiallyRefilled: true } : {}),
      },
    ];
  });
};
